import { select, selectAll } from 'd3c';
import Sentiment from 'Sentiment';

export default class GeomapArcs {
	constructor(options) {
		if (typeof options === 'undefined') {
			throw new Error('GeoMap Error - arcs handler must have options');
		}
		this.options = options;
		this._state = {
			arcsShown: options.drawArcsOnInit,
			updating: false,
			zoomScale: 1
		};
		this.svgContainer = select(this.options.element).select('svg');
		this.layer = this.svgContainer
			.select('.geomap_zoomer')
			.append('g')
			.attr('class', 'geomap-arcs');

		this.layer
			.append('g')
			.attr('id', 'gradients');
	}

	init() {}

	enable(enable) {
		this._state.arcsShown = enable;
	}

	update(geomapState, withAnimation) {
		if (!this._state.arcsShown) {
			this.layer
				.selectAll('path.datamaps-arc')
				.remove();
			return;
		}
		const _this = this;
		const options = this.options.arcsConfig;
		const data = geomapState._data.links;
		if (!Array.isArray(data)) {
			throw new Error('GeoMap Error - arcs must be an array');
		}

		const arcs = this.layer
			.selectAll('.datamaps-arc')
			.data(data, d => `${d.id}_${d.color}`);
		const arcsExit = arcs.exit();

		if (withAnimation) {
			arcsExit.style('fill', (d, i) => `url(#gradFill${i}-exit)`);
			arcsExit
				.transition()
				.duration(this.options.animationDuration)
				.style('stroke-opacity', 0)
				.style('opacity', 0)
				.remove();
		} else {
			arcsExit.remove();
		}

		let enterArcs = arcs
			.enter()
			.append('svg:path')
			.attr('class', 'datamaps-arc');

		enterArcs
			.style('fill-opacity', 0)
			.style('stroke-opacity', 0)
			.merge(arcs)
			.style(options.drawLines ? 'fill-opacity' : 'stroke-opacity', 0)
			.call(d => this._updateArcsPath(d, geomapState, withAnimation))
			.style('stroke-linecap', 'round')
			.style('pointer-events', 'none') // arcs don't listen to mouse events
			.style(options.drawLines ? 'stroke' : 'fill', (d, i) => {
				// fill color only for tapered shapes
				if (options.colorType === 'solid') {
					if (_this._state.updating) {
						return d.updateColor ? d.updateColor : _this.options.updateColor;
					} else {
						return geomapState.colorMode === 'sentiment' ? Sentiment.getColor(d.sentiment, 'global') : d.color;
					}
				} else {
					return `url(#gradFill${i})`;
				}
			})
			.style('stroke-width', function(datum) {
				if (options.drawLines) {
					// stroke width only for lines
					if (options.showWeight) {
						return datum.weight; // stroke width = weight if specified
					}
					return options.strokeWidth / _this._state.zoomScale;
				} else {
					return 0;
				}
			}.bind(this)); // stroke opacity only for lines

		enterArcs = enterArcs
			.merge(arcs);

		if (withAnimation) {
			enterArcs = enterArcs
				.transition()
				.duration(this.options.animationDuration);
		}
		enterArcs.style(
			options.drawLines ? 'stroke-opacity' : 'fill-opacity',
			options.drawLines ? options.strokeOpacity : options.fillOpacity
		);

		// gradient fill
		if (options.colorType === 'gradient') {
			this._updateGradients(data, geomapState, withAnimation);
		}
	}

	_updateGradients(data, geomapState, withAnimation) {
		const options = this.options.arcsConfig;
		const _this = this;
		let gradients = select('#gradients')
			.selectAll('linearGradient.grads')
			.data(data, d => `${d.id}_${d.color}`);

		if (withAnimation) {
			gradients
				.exit()
				.attr('id', (d, i) => `gradFill${i}-exit`)
				.transition()
				.duration(this.options.animationDuration)
				.remove();
		} else {
			gradients
				.exit()
				.remove();
		}

		gradients = gradients
			.enter()
			.append('linearGradient')
			.attr('class', 'grads')
			.merge(gradients);

		gradients
			.attr('id', function(d, i) {
				return `gradFill${i}`;
			})
			.attr('x1', function(d) {
				return d.stops.src[0];
			})
			.attr('y1', function(d) {
				return d.stops.src[1];
			})
			.attr('x2', function(d) {
				return d.stops.tgt[0];
			})
			.attr('y2', function(d) {
				return d.stops.tgt[1];
			})
			.attr('spreadMethod', 'pad')
			.attr('gradientUnits', 'userSpaceOnUse');

		// Clean up stop after greyout
		gradients.selectAll('.gradient-greyed').remove();

		gradients
			.append('svg:stop')
			.attr('offset', '10%') // gradient position
			.attr('stop-color', function(d) {
				const solidColor = geomapState.colorMode === 'sentiment'
					? Sentiment.getColor(d.sentiment, 'global')
					: d.color; // gradient color

				if (_this._state.updating) {
					return d.updateColor ? d.updateColor : _this.options.updateColor;
				} else {
					return options.gradientOpaque ? options.gradientOpaqueColor : solidColor;
				}
			})
			.attr('stop-opacity', options.gradientOpaque ? 1 : 0); // gradient opacity

		gradients
			.append('svg:stop')
			.attr('offset', '50%')
			.attr('stop-color', function(d) {
				if (_this._state.updating) {
					return d.updateColor ? d.updateColor : _this.options.updateColor;
				} else {
					return geomapState.colorMode === 'sentiment' ? Sentiment.getColor(d.sentiment, 'global') : d.color;
				}
			})
			.attr('stop-opacity', 1);
	}

	_updateArcsPath(selection, geomapState, withAnimation) {
		const options = this.options.arcsConfig;
		const selectionToUpdate = withAnimation
			? selection.transition().duration(this.options.animationDuration)
			: selection;
		selectionToUpdate.attr('d', datum => {
			const src = geomapState.projection([datum.origin.longitude, datum.origin.latitude]);
			const tgt = geomapState.projection([datum.destination.longitude, datum.destination.latitude]);
			const mid = [
				(src[0] + tgt[0]) / 2,
				(src[1] + tgt[1]) / 2
			];
			// calculate the distance between source and target
			const dist = Math.sqrt((tgt[0] - src[0]) * (tgt[0] - src[0]) + (tgt[1] - src[1]) * (tgt[1] - src[1]));
			// add source and target locations to the data options
			datum.stops = {
				src,
				tgt
			};

			if (options.drawLines) {
				return getLinePath(options, src, mid, tgt, dist);
			} else {
				return getTaperedPath(options, geomapState, datum, src, tgt, dist, this._state.zoomScale);
			}
		});
	}

	zoomed(geomapState, currentZoomTransform, isZoomChanged) {
		this._state.zoomScale = currentZoomTransform.k;
		if (isZoomChanged) {
			this._updateArcsPath(this.layer.selectAll('path.datamaps-arc'), geomapState);
		}
	}

	setUpdatingState(updating) {
		this._state.updating = updating;
	}

	// function to highlight arcs for specified bubble
	highlightBubbleArcs(data) {
		const dataObj = Array.isArray(data) ? data[0] : data;

		this.svgContainer
			.selectAll('.datamaps-arc')
			.style(this.options.arcsConfig.drawLines ? 'stroke-opacity' : 'fill-opacity', d => {
				const highlightOpacity = this.options.arcsConfig.drawLines
					? this.options.arcsConfig.highlightStrokeOpacity
					: this.options.arcsConfig.highlightFillOpacity;
				const defaultOpacity = this.options.arcsConfig.drawLines
					? this.options.arcsConfig.strokeOpacity
					: this.options.arcsConfig.fillOpacity;
				return dataObj && d.destination && haveSameCoords(d.destination, dataObj)
					? highlightOpacity
					: defaultOpacity;
			})
			.each(function(d) {
				d.hover = haveSameCoords(d.destination, dataObj);
			});
	}

	// dehighlight country links
	dehighlightArcs() {
		selectAll('.datamaps-arc')
			.style(
				this.options.arcsConfig.drawLines ? 'stroke-opacity' : 'fill-opacity',
				this.options.arcsConfig.drawLines ? this.options.arcsConfig.strokeOpacity : this.options.arcsConfig.fillOpacity
			)
			.each(function(d) {
				d.hover = false;
			});
	}

	// highlight country arcs
	highlightCountryArcs(code) {
		if (code == null) {
			return;
		}

		const arcs = selectAll('.datamaps-arc');

		arcs
			.style(this.options.arcsConfig.drawLines ? 'stroke-opacity' : 'fill-opacity', d => {
				const highlightOpacity = this.options.arcsConfig.drawLines
					? this.options.arcsConfig.highlightStrokeOpacity
					: this.options.arcsConfig.highlightFillOpacity;
				const defaultOpacity = this.options.arcsConfig.drawLines
					? this.options.arcsConfig.strokeOpacity
					: this.options.arcsConfig.fillOpacity;
				return d.source === code ? highlightOpacity : defaultOpacity;
			})
			.each(function(d) {
				d.hover = d.source === code ? true : false;
			});
	}
}

const getLinePath = (options, src, mid, tgt, dist) => {
	// straight lines
	if (options.lineType === 'straight') {
		return `M${src[0]} ${src[1]}C${mid[0]} ${mid[1]} ${mid[0]} ${mid[1]} ${tgt[0]} ${tgt[1]}`;
	} else if (options.lineType === 'elliptical') {
		return src[0] < tgt[0]
			? `M${src[0]},${src[1]}A${dist},${dist} 0 0,1 ${tgt[0]},${tgt[1]}`
			: `M${src[0]},${src[1]}A${dist},${dist} 0 0,0 ${tgt[0]},${tgt[1]}`;
	} else if (options.lineType === '90straight') {
		return `M${src[0]},${src[1]}L${src[0]},${tgt[1]}L${tgt[0]},${tgt[1]}`;
	} else if (options.lineType === '90+90straight') {
		const svgH = select('svg').attr('height');
		return src[1] <= svgH / 2
			? `M${src[0]},${src[1]}L${src[0]},${src[1] - dist / 10}L${tgt[0]},${src[1] - dist / 10}L${tgt[0]},${tgt[1]}`
			: `M${src[0]},${src[1]}L${src[0]},${src[1] + dist / 10}L${tgt[0]},${src[1] + dist / 10}L${tgt[0]},${tgt[1]}`;
	} else if (options.lineType === '90+90curved') {
		const svgH = select('svg').attr('height');
		return src[1] <= svgH / 2
			? `M${src[0]},${src[1]}C${src[0]},${50 - src[1] / 10},${tgt[0]},${50 - src[1] / 10} ${tgt[0]},${tgt[1]}`
			: `M${src[0]},${src[1]}C${src[0]},${svgH - 100 + src[1] / 10},${tgt[0]},${svgH - 100 + src[1] / 10},${tgt[0]},${tgt[1]}`;
	} else {
		throw new Error(`Arcs line type [${options.lineType}] is not supported`);
	}
};

const getTaperedPath = (options, geomapState, datum, src, tgt, dist, zoomScale) => {
	// draw tapered shapes
	// calculate the angle of rotation
	const dx = tgt[0] - src[0];
	const dy = tgt[1] - src[1];
	const ang = Math.atan2(dy, dx); // * 180 / Math.PI

	// get radius
	const r = options.showWeight
		? datum.weight / zoomScale / 2
		: geomapState.radius(datum.width) / zoomScale / 2; // divide radius by 2 to get narrower arcs

	let i1 = r,
		i2 = 0.5 / zoomScale;
	if (options.invertDirection) {
		// parameter for inverted direction
		i1 = i2;
		i2 = r;
	}
	if (options.shapeType === 'straight') {
		return getTaperedStraightPath(src, tgt, ang, i1, i2);
	} else if (options.shapeType === 'elliptical') {
		return getTaperedElipticalPath(src, tgt, ang, i1, i2, dist);
	} else if (options.shapeType === 'elliptical+mirrored') {
		return getTaperedElipticalMirroredPath(src, tgt, ang, i1, i2, dist);
	} else if (options.shapeType === 'curved+balanced') {
		return getCurvedBalancedPath(src, tgt, i1, i2, dist, r);
	} else if (options.shapeType === 'curved+balanced+mirroredV') {
		return getCurvedBalancedMirrowedVPath(src, tgt, i1, i2, dist, r);
	} else if (options.shapeType === 'curved+balanced+mirroredH') {
		return getCurvedBalancedMirrowedHPath(src, tgt, i1, i2, dist, r);
	} else if (options.shapeType === 'flow') {
		return getTaperedFlowPath(src, tgt, ang, i1, i2, dx, dy, r);
	} else {
		throw new Error(`Arcs shape type [${options.shapeType}] is not supported`);
	}
};

// tapered straight lines
const getTaperedStraightPath = (src, tgt, ang, i1, i2) => {
	// rotated points for source
	const P1 = [Math.cos(ang - Math.PI / 2) * i1 + src[0], Math.sin(ang - Math.PI / 2) * i1 + src[1]];
	const P2 = [Math.cos(ang + Math.PI / 2) * i1 + src[0], Math.sin(ang + Math.PI / 2) * i1 + src[1]];
	// rotated points for target
	const P3 = [Math.cos(ang - Math.PI / 2) * i2 + tgt[0], Math.sin(ang - Math.PI / 2) * i2 + tgt[1]];
	const P4 = [Math.cos(ang + Math.PI / 2) * i2 + tgt[0], Math.sin(ang + Math.PI / 2) * i2 + tgt[1]];
	return `M${P1[0]},${P1[1]}L${P3[0]},${P3[1]}L${P4[0]},${P4[1]}L${P2[0]},${P2[1]}`;
};

const getTaperedElipticalPath = (src, tgt, ang, i1, i2, dist) => {
	// vertices and control points
	let P1, P2, P3, P4, CP1, CP2, CP3, CP4;
	if (src[0] < tgt[0]) {
		// rotated points for source
		P1 = [
			Math.cos(ang - Math.PI / 2 - Math.PI / 4) * i1 + src[0],
			Math.sin(ang - Math.PI / 2 - Math.PI / 4) * i1 + src[1]
		];
		P2 = [
			Math.cos(ang + Math.PI / 2 - Math.PI / 4) * i1 + src[0],
			Math.sin(ang + Math.PI / 2 - Math.PI / 4) * i1 + src[1]
		];
		// rotated points for target
		P3 = [Math.cos(ang - Math.PI / 4) * i2 + tgt[0], Math.sin(ang - Math.PI / 4) * i2 + tgt[1]];
		P4 = [
			Math.cos(ang + Math.PI / 2 + Math.PI / 4) * i2 + tgt[0],
			Math.sin(ang + Math.PI / 2 + Math.PI / 4) * i2 + tgt[1]
		];
		// upper arc control points
		CP1 = [(Math.cos(ang - Math.PI / 4) * dist) / 3 + P1[0], (Math.sin(ang - Math.PI / 4) * dist) / 3 + P1[1]];
		CP2 = [
			(Math.cos(ang - Math.PI / 2 - Math.PI / 4) * dist) / 3 + P3[0],
			(Math.sin(ang - Math.PI / 2 - Math.PI / 4) * dist) / 3 + P3[1]
		];
		// lower arc control points
		CP3 = [
			(Math.cos(ang - Math.PI / 2 - Math.PI / 4) * dist) / 3 + P4[0],
			(Math.sin(ang - Math.PI / 2 - Math.PI / 4) * dist) / 3 + P4[1]
		];
		CP4 = [(Math.cos(ang - Math.PI / 4) * dist) / 3 + P2[0], (Math.sin(ang - Math.PI / 4) * dist) / 3 + P2[1]];
	} else {
		ang += Math.PI;
		// rotated points for source
		P1 = [Math.cos(ang - Math.PI / 4) * i1 + src[0], Math.sin(ang - Math.PI / 4) * i1 + src[1]];
		P2 = [
			Math.cos(ang + Math.PI / 2 + Math.PI / 4) * i1 + src[0],
			Math.sin(ang + Math.PI / 2 + Math.PI / 4) * i1 + src[1]
		];
		// rotated points for target
		P3 = [
			Math.cos(ang - Math.PI / 2 - Math.PI / 4) * i2 + tgt[0],
			Math.sin(ang - Math.PI / 2 - Math.PI / 4) * i2 + tgt[1]
		];
		P4 = [Math.cos(ang + Math.PI / 4) * i2 + tgt[0], Math.sin(ang + Math.PI / 4) * i2 + tgt[1]];
		// upper arc control points
		CP1 = [
			(Math.cos(ang - Math.PI / 2 - Math.PI / 4) * dist) / 3 + P1[0],
			(Math.sin(ang - Math.PI / 2 - Math.PI / 4) * dist) / 3 + P1[1]
		];
		CP2 = [(Math.cos(ang - Math.PI / 4) * dist) / 3 + P3[0], (Math.sin(ang - Math.PI / 4) * dist) / 3 + P3[1]];
		// lower arc control points
		CP3 = [(Math.cos(ang - Math.PI / 4) * dist) / 3 + P4[0], (Math.sin(ang - Math.PI / 4) * dist) / 3 + P4[1]];
		CP4 = [
			(Math.cos(ang - Math.PI / 2 - Math.PI / 4) * dist) / 3 + P2[0],
			(Math.sin(ang - Math.PI / 2 - Math.PI / 4) * dist) / 3 + P2[1]
		];
	}

	return `M${P1[0]},${P1[1]}C${CP1[0]},${CP1[1]},${CP2[0]},${CP2[1]},${P3[0]},${P3[1]}L${P4[0]},${P4[1]}C${CP3[0]},${CP3[1]},${CP4[0]},${CP4[1]},${P2[0]},${P2[1]}`;
};

const getTaperedElipticalMirroredPath = (src, tgt, ang, i1, i2, dist) => {
	// tapered mirrored arcs
	// rotated points for source
	const P1 = [
		Math.cos(ang - Math.PI / 2 - Math.PI / 4) * i1 + src[0],
		Math.sin(ang - Math.PI / 2 - Math.PI / 4) * i1 + src[1]
	];
	const P2 = [
		Math.cos(ang + Math.PI / 2 - Math.PI / 4) * i1 + src[0],
		Math.sin(ang + Math.PI / 2 - Math.PI / 4) * i1 + src[1]
	];
	// rotated points for target
	const P3 = [Math.cos(ang - Math.PI / 4) * i2 + tgt[0], Math.sin(ang - Math.PI / 4) * i2 + tgt[1]];
	const P4 = [
		Math.cos(ang + Math.PI / 2 + Math.PI / 4) * i2 + tgt[0],
		Math.sin(ang + Math.PI / 2 + Math.PI / 4) * i2 + tgt[1]
	];
	// upper arc control points
	const CP1 = [(Math.cos(ang - Math.PI / 4) * dist) / 3 + P1[0], (Math.sin(ang - Math.PI / 4) * dist) / 3 + P1[1]];
	const CP2 = [
		(Math.cos(ang - Math.PI / 2 - Math.PI / 4) * dist) / 3 + P3[0],
		(Math.sin(ang - Math.PI / 2 - Math.PI / 4) * dist) / 3 + P3[1]
	];
	// lower arc control points
	const CP3 = [
		(Math.cos(ang - Math.PI / 2 - Math.PI / 4) * dist) / 3 + P4[0],
		(Math.sin(ang - Math.PI / 2 - Math.PI / 4) * dist) / 3 + P4[1]
	];
	const CP4 = [(Math.cos(ang - Math.PI / 4) * dist) / 3 + P2[0], (Math.sin(ang - Math.PI / 4) * dist) / 3 + P2[1]];

	return `M${P1[0]},${P1[1]}C${CP1[0]},${CP1[1]},${CP2[0]},${CP2[1]},${P3[0]},${P3[1]}L${P4[0]},${P4[1]}C${CP3[0]},${CP3[1]},${CP4[0]},${CP4[1]},${P2[0]},${P2[1]}`;
};

const getCurvedBalancedPath = (src, tgt, i1, i2, dist, r) => {
	// tapered arcs with balanced control points
	// rotated points for source
	const P1 = [Math.cos(Math.PI) * i1 + src[0], Math.sin(Math.PI) * i1 + src[1]];
	const P2 = [Math.cos(0) * i1 + src[0], Math.sin(0) * i1 + src[1]];
	// rotated points for target
	const P3 = [Math.cos(0) * i2 + tgt[0], Math.sin(0) * i2 + tgt[1]];
	const P4 = [Math.cos(Math.PI) * i2 + tgt[0], Math.sin(Math.PI) * i2 + tgt[1]];
	// upper arc control points
	const CP1 = [Math.cos(Math.PI) * r + P1[0], Math.sin(Math.PI) * r - dist / 3 + P1[1]];
	const CP2 = [Math.cos(0) * 0.5 + P3[0], Math.sin(0) * 0.5 - dist / 3 + P3[1]];
	// lower arc control points
	const CP3 = [Math.cos(Math.PI) * 0.5 + P4[0], Math.sin(Math.PI) * 0.5 - dist / 3 + P4[1]];
	const CP4 = [Math.cos(0) * r + P2[0], Math.sin(0) * r - dist / 3 + P2[1]];

	// add height difference depending on the y-position
	if (src[1] > tgt[1]) {
		CP1[1] -= src[1] - tgt[1];
		CP4[1] -= src[1] - tgt[1];
	} else {
		CP2[1] -= tgt[1] - src[1];
		CP3[1] -= tgt[1] - src[1];
	}
	// add height difference depending on the x-position
	if (src[0] < tgt[0]) {
		CP1[1] -= 4;
		CP2[1] -= 4;
	} else {
		CP3[1] -= 4;
		CP4[1] -= 4;
	}

	return `M${P1[0]},${P1[1]}C${CP1[0]},${CP1[1]},${CP2[0]},${CP2[1]},${P3[0]},${P3[1]}L${P4[0]},${P4[1]}C${CP3[0]},${CP3[1]},${CP4[0]},${CP4[1]},${P2[0]},${P2[1]}`;
};

const getCurvedBalancedMirrowedVPath = (src, tgt, i1, i2, dist, r) => {
	// tapered arcs with balanced control points, mirrored if target is lower than source
	// rotated points for source
	const P1 = [Math.cos(Math.PI) * i1 + src[0], Math.sin(Math.PI) * i1 + src[1]];
	const P2 = [Math.cos(0) * i1 + src[0], Math.sin(0) * i1 + src[1]];
	// rotated points for target
	const P3 = [Math.cos(0) * i2 + tgt[0], Math.sin(0) * i2 + tgt[1]];
	const P4 = [Math.cos(Math.PI) * i2 + tgt[0], Math.sin(Math.PI) * i2 + tgt[1]];
	// upper arc control points
	const CP1 = [Math.cos(Math.PI) * r + P1[0], Math.sin(Math.PI) * r + P1[1]];
	const CP2 = [Math.cos(0) * 0.5 + P3[0], Math.sin(0) * 0.5 + P3[1]];
	// lower arc control points
	const CP3 = [Math.cos(Math.PI) * 0.5 + P4[0], Math.sin(Math.PI) * 0.5 + P4[1]];
	const CP4 = [Math.cos(0) * r + P2[0], Math.sin(0) * r + P2[1]];

	// add height difference depending on the y-position
	if (src[1] > tgt[1]) {
		CP1[1] -= src[1] - tgt[1];
		CP4[1] -= src[1] - tgt[1];
		CP1[1] -= dist / 3;
		CP2[1] -= dist / 3;
		CP3[1] -= dist / 3;
		CP4[1] -= dist / 3;
		// add extra height depending on the x-position and mirror the arc
		if (src[0] < tgt[0]) {
			CP1[1] -= 4;
			CP2[1] -= 4;
		} else {
			CP3[1] -= 4;
			CP4[1] -= 4;
		}
	} else {
		CP1[1] += tgt[1] - src[1];
		CP4[1] += tgt[1] - src[1];
		CP1[1] += dist / 3;
		CP2[1] += dist / 3;
		CP3[1] += dist / 3;
		CP4[1] += dist / 3;
		// add extra height depending on the x-position and mirror the arc
		if (src[0] < tgt[0]) {
			CP1[1] += 4;
			CP2[1] += 4;
		} else {
			CP3[1] += 4;
			CP4[1] += 4;
		}
	}

	return `M${P1[0]},${P1[1]}C${CP1[0]},${CP1[1]},${CP2[0]},${CP2[1]},${P3[0]},${P3[1]}L${P4[0]},${P4[1]}C${CP3[0]},${CP3[1]},${CP4[0]},${CP4[1]},${P2[0]},${P2[1]}`;
};

const getCurvedBalancedMirrowedHPath = (src, tgt, i1, i2, dist, r) => {
	// tapered arcs with balanced control points, mirrored if target is to the left of the source
	// rotated points for source
	const P1 = [Math.cos(Math.PI) * i1 + src[0], Math.sin(Math.PI) * i1 + src[1]];
	const P2 = [Math.cos(0) * i1 + src[0], Math.sin(0) * i1 + src[1]];
	// rotated points for target
	const P3 = [Math.cos(0) * i2 + tgt[0], Math.sin(0) * i2 + tgt[1]];
	const P4 = [Math.cos(Math.PI) * i2 + tgt[0], Math.sin(Math.PI) * i2 + tgt[1]];
	// upper arc control points
	const CP1 = [Math.cos(Math.PI) * r + P1[0], Math.sin(Math.PI) * r + P1[1]];
	const CP2 = [Math.cos(0) * 0.5 + P3[0], Math.sin(0) * 0.5 + P3[1]];
	// lower arc control points
	const CP3 = [Math.cos(Math.PI) * 0.5 + P4[0], Math.sin(Math.PI) * 0.5 + P4[1]];
	const CP4 = [Math.cos(0) * r + P2[0], Math.sin(0) * r + P2[1]];

	if (src[1] > tgt[1]) {
		if (src[0] < tgt[0]) {
			// add height difference depending on the y-position
			CP1[1] -= src[1] - tgt[1];
			CP4[1] -= src[1] - tgt[1];
			CP1[1] -= dist / 3;
			CP2[1] -= dist / 3;
			CP3[1] -= dist / 3;
			CP4[1] -= dist / 3;
			// add extra height depending on the x-position and mirror the arc
			CP1[1] -= r * 2;
			CP2[1] -= r * 2;
		} else {
			CP2[1] += src[1] - tgt[1];
			CP3[1] += src[1] - tgt[1];
			CP1[1] += dist / 3;
			CP2[1] += dist / 3;
			CP3[1] += dist / 3;
			CP4[1] += dist / 3;
			CP3[1] += r * 2;
			CP4[1] += r * 2;
		}
	} else {
		if (src[0] < tgt[0]) {
			CP2[1] -= tgt[1] - src[1];
			CP3[1] -= tgt[1] - src[1];
			CP1[1] -= dist / 3;
			CP2[1] -= dist / 3;
			CP3[1] -= dist / 3;
			CP4[1] -= dist / 3;
			CP1[1] -= r * 2;
			CP2[1] -= r * 2;
		} else {
			CP1[1] += tgt[1] - src[1];
			CP4[1] += tgt[1] - src[1];
			CP1[1] += dist / 3;
			CP2[1] += dist / 3;
			CP3[1] += dist / 3;
			CP4[1] += dist / 3;
			CP3[1] += r * 2;
			CP4[1] += r * 2;
		}
	}

	return `M${P1[0]},${P1[1]}C${CP1[0]},${CP1[1]},${CP2[0]},${CP2[1]},${P3[0]},${P3[1]}L${P4[0]},${P4[1]}C${CP3[0]},${CP3[1]},${CP4[0]},${CP4[1]},${P2[0]},${P2[1]}`;
};

const getDirecionsForControlPoints = ang =>{
	if (ang >= Math.PI / 4 && ang < Math.PI / 4 + Math.PI / 2) {
		return [-Math.PI / 2, Math.PI / 2];
	} else if ((ang >= Math.PI / 4 + Math.PI / 2 && ang < Math.PI / 4 + Math.PI) || (ang <= -Math.PI / 4 - Math.PI / 2 && ang > -Math.PI / 4 - Math.PI)) {
		return [-Math.PI, 0];
	} else if (ang <= -Math.PI / 4 && ang > -Math.PI / 4 - Math.PI / 2) {
		return [Math.PI / 2, -Math.PI / 2];
	} else {
		return [0, -Math.PI];
	}
};

// tapered flowmap curves
const getTaperedFlowPath = (src, tgt, ang, i1, i2, dx, dy, r) => {
	const [dirS, dirT] = getDirecionsForControlPoints(ang);

	// vertices
	const P1 = [Math.cos(dirS - Math.PI / 2) * i1 + src[0], Math.sin(dirS - Math.PI / 2) * i1 + src[1]];
	const P2 = [Math.cos(dirT + Math.PI / 2) * i2 + tgt[0], Math.sin(dirT + Math.PI / 2) * i2 + tgt[1]];
	const P3 = [Math.cos(dirT - Math.PI / 2) * i2 + tgt[0], Math.sin(dirT - Math.PI / 2) * i2 + tgt[1]];
	const P4 = [Math.cos(dirS + Math.PI / 2) * i1 + src[0], Math.sin(dirS + Math.PI / 2) * i1 + src[1]];
	// control points (same)
	const CP1 = [Math.cos(dirS - Math.PI / 2) * r + src[0], Math.sin(dirS - Math.PI / 2) * r + src[1]];
	const CP2 = [Math.cos(dirT + Math.PI / 2) * 0.5 + tgt[0], Math.sin(dirT + Math.PI / 2) * 0.5 + tgt[1]];
	const CP3 = [Math.cos(dirT - Math.PI / 2) * 0.5 + tgt[0], Math.sin(dirT - Math.PI / 2) * 0.5 + tgt[1]];
	const CP4 = [Math.cos(dirS + Math.PI / 2) * r + src[0], Math.sin(dirS + Math.PI / 2) * r + src[1]];

	if (dx < 0) {
		dx *= -1;
	}
	if (dy < 0) {
		dy *= -1;
	}

	if (dirS === Math.PI / 2 && dirT === -Math.PI / 2) {
		CP1[1] -= dy / 2;
		CP2[1] += dy / 2;
		CP3[1] += dy / 2;
		CP4[1] -= dy / 2;
	} else if (dirS === -Math.PI && dirT === 0) {
		CP1[0] -= dx / 2;
		CP2[0] += dx / 2;
		CP3[0] += dx / 2;
		CP4[0] -= dx / 2;
	} else if (dirS === -Math.PI / 2 && dirT === Math.PI / 2) {
		CP1[1] += dy / 2;
		CP2[1] -= dy / 2;
		CP3[1] -= dy / 2;
		CP4[1] += dy / 2;
	} else {
		CP1[0] += dx / 2;
		CP2[0] -= dx / 2;
		CP3[0] -= dx / 2;
		CP4[0] += dx / 2;
	}

	return `M${P1[0]},${P1[1]}C${CP1[0]},${CP1[1]},${CP2[0]},${CP2[1]},${P2[0]},${P2[1]}L${P3[0]},${P3[1]}C${CP3[0]},${CP3[1]},${CP4[0]},${CP4[1]},${P4[0]},${P4[1]}`;
};

const haveSameCoords = (a, b) => a.latitude === b.latitude && a.longitude === b.longitude;
